import { UrlConst } from 'src/app/shared/constants/url-const';
import { Site } from 'src/app/shared/interfaces/site';
import { AccessLogService } from 'src/app/shared/services/access-log.service';
import { AccountService } from 'src/app/shared/services/account.service';
import { SensorService } from 'src/app/shared/services/sensor.service';
import { SiteService } from 'src/app/shared/services/site.service';
import { UtilFunctions } from 'src/app/shared/utils/util-functions';

import { Injectable } from '@angular/core';
import { deleteField } from 'firebase/firestore';
import { LatestTiltSensorData } from 'src/app/shared/interfaces/latest-tilt-sensor-data';

@Injectable({
  providedIn: 'root',
})
export class SensorTiltFormService {
  constructor(
    private accessLogService: AccessLogService,
    private accountService: AccountService,
    private siteService: SiteService,
    private sensorService: SensorService
  ) {}

  addLog() {
    return this.accessLogService.addLog(
      UrlConst.SLASH +
        UrlConst.PATH_SITE_CONFIG +
        UrlConst.SLASH +
        UrlConst.PATH_SITE_CONFIG_SENSOR
    );
  }

  isValidUser() {
    return (
      this.accountService.isSysAdminGroup() ||
      this.accountService.isGeneralGroup()
    );
  }

  isGeneralGroup() {
    return this.accountService.isGeneralGroup();
  }

  getUser() {
    return this.accountService.getUser();
  }

  getSite(id: string) {
    return this.siteService.get(id);
  }

  getSiteWithOrganization(id: string) {
    return this.siteService.getWithOrganization(
      id,
      this.accountService.getOrganizationId()
    );
  }

  getSensor(id: string) {
    return this.sensorService.get(id);
  }

  getNoConfigSensors() {
    return this.sensorService.getAllWindNoConfig();
  }

  getNoConfigSensorsWithOrganization() {
    return this.sensorService.getAllTiltNoConfigWithOrganization(
      this.accountService.getOrganizationId()
    );
  }

  //
  // setSensorToSite
  //
  // 現場へのセンサー設定
  // 1. 該当センサーデータに対して現場IDと設置場所を設定する
  // 2. 現場に対してセンサーのリファレンスを追加する
  // 3. 最新データに対してセンサー情報を設定する
  //
  async setSensorToSite(
    site: Site,
    sensorId: string,
    positionName: string,
    heightFromBottom?: number,
    managementStandard?: number,
    hourlyDisplacementStandard?: number,
    standard?: {
      tiltX: number;
      tiltY: number;
      date?: string;
    },
    threshold?: { tiltX: number; tiltY: number },
    isUpdate: boolean = false,
    isStandardUpdated: boolean = false
  ) {
    // 日本時間の現在日時を取得
    const formattedDate = UtilFunctions.now();

    // センサー情報更新
    const updateData: any = {
      siteId: site.id,
      positionName: positionName,
      heightFromBottom: heightFromBottom,
      managementStandard: managementStandard,
      hourlyDisplacementStandard: hourlyDisplacementStandard,
      updatedAt: formattedDate,
    };

    // 新規追加の場合はcreatedAtを設定
    if (!isUpdate) {
      updateData.createdAt = formattedDate;
    }

    // standardが指定されている場合のみ更新
    if (standard !== undefined) {
      updateData.standard = {
        tiltX: standard.tiltX ?? 0,
        tiltY: standard.tiltY ?? 0,
      };

      // dateが指定されている場合は追加
      if (standard.date) {
        updateData.standard.date = standard.date;
      }
    }

    if (threshold) {
      // undefined値をフィルタリング
      updateData.threshold = {
        tiltX: threshold.tiltX ?? 0,
        tiltY: threshold.tiltY ?? 0,
      };
    }

    // 基準値更新時 / ゼロセット保存時: アラート情報と変位量をクリア
    if (isStandardUpdated) {
      updateData['latestData.hasManagementAlert'] = false;
      updateData['latestData.managementAlertDate'] = deleteField();
      updateData['latestData.managementAlertValue'] = deleteField();
      updateData['latestData.managementClearedDate'] = deleteField();
      updateData['latestData.hasHourlyAlert'] = false;
      updateData['latestData.hourlyAlertDate'] = deleteField();
      updateData['latestData.hourlyAlertValue'] = deleteField();
      updateData['latestData.hourlyClearedDate'] = deleteField();
      updateData['latestData.hasAttention'] = false;
      updateData['latestData.attentionDate'] = deleteField();
      updateData['latestData.attentionValue'] = deleteField();
      updateData['latestData.attentionClearedDate'] = deleteField();
      updateData['latestData.displacement'] = 0;
      updateData['latestData.displacementX'] = 0;
      updateData['latestData.displacementY'] = 0;
      updateData['latestData.hourlyDeltaDisplacementX'] = 0;
      updateData['latestData.hourlyDeltaTiltX'] = 0;
    }

    await this.sensorService.update(sensorId, updateData);

    // 現場情報更新
    await this.siteService.updateTiltSensorRef(
      site,
      this.sensorService.getDocumentReference(sensorId)
    );

    // 最新データ更新
    const latestHistory = await this.sensorService.getLatestHistory(sensorId);
    const request: LatestTiltSensorData = {
      id: sensorId,
      positionName: positionName,
    };

    if (managementStandard !== undefined) {
      request.managementStandard = managementStandard;
    }

    if (latestHistory !== null) {
      // undefined値を安全に処理
      if (latestHistory.date !== undefined) {
        request.date = latestHistory.date;
      }
      if (latestHistory.tiltX !== undefined) {
        request.tiltX = latestHistory.tiltX;
      }
      if (latestHistory.tiltY !== undefined) {
        request.tiltY = latestHistory.tiltY;
      }
    }

    await this.siteService.updateLatestTiltSensorData(site.id, request);

    // 基準値更新時 / ゼロセット保存時: latest_site_data のアラート情報もクリア
    if (isStandardUpdated) {
      await this.siteService.clearLatestSiteDataTiltAlerts(site.id, sensorId);
    }
  }
}
